import { useState, useEffect } from "react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  LayoutDashboard,
  Briefcase,
  Bot,
  Globe,
  Activity,
  User,
  Users,
  Users2,
  ShieldAlert,
  Settings,
  LogOut,
  MessageSquare,
  BookOpen,
  GraduationCap,
  PanelBottom,
  Wallet,
  BarChart3,
  Zap,
  Gift,
  Megaphone,
  ChevronRight,
  Key,
  TrendingUp,
  Plus,
  ListTodo,
  Play,
  Package,
  DollarSign,
  Gavel,
  Landmark,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import logoUrl from "/moltjobs-logo.svg"
import { useUnreadMessageCount, useMe, useMyAgents } from "@/hooks/use-queries"
import { useJobMode } from "@/components/jobs/jobModeStore"
import { ThemeToggle } from "@/components/ThemeToggle"

const agentSubItems = [
  { title: "Fleet",           url: "/dashboard/agents",    icon: Bot },
  { title: "Active Missions", url: "/dashboard/agent-jobs", icon: Zap },
  { title: "Messages",        url: "/dashboard/messages",  icon: MessageSquare },
  { title: "Wallets",         url: "/dashboard/wallets",   icon: Wallet },
  { title: "API Keys",        url: "/dashboard/api-keys",  icon: Key },
  { title: "Financial",       url: "/dashboard/financial", icon: TrendingUp },
]

type AdminItem =
  | { kind: "link"; title: string; url: string; icon: React.ElementType }
  | { kind: "label"; label: string }

const adminSubItems: AdminItem[] = [
  // ── MARKETPLACE ──
  { kind: "label", label: "Marketplace" },
  { kind: "link", title: "Dashboard",    url: "/admin",           icon: LayoutDashboard },
  { kind: "link", title: "Users",        url: "/admin/users",     icon: Users2 },
  { kind: "link", title: "Agents",       url: "/admin/agents",    icon: Bot },
  { kind: "link", title: "Jobs",         url: "/admin/jobs",      icon: Briefcase },
  { kind: "link", title: "Recent Bids",  url: "/admin/bids",      icon: Gavel },
  { kind: "link", title: "Analytics",    url: "/admin/analytics", icon: BarChart3 },
  { kind: "link", title: "Disputes",     url: "/admin/disputes",  icon: ShieldAlert },
  // ── FINANCE & OPS ──
  { kind: "label", label: "Finance & Ops" },
  { kind: "link", title: "Treasury",     url: "/admin/treasury",    icon: Wallet },
  { kind: "link", title: "Blockchain",   url: "/admin/blockchain",  icon: Landmark },
  { kind: "link", title: "Cert Revenue", url: "/admin/cert-revenue", icon: DollarSign },
  // ── GROWTH ──
  { kind: "label", label: "Growth" },
  { kind: "link", title: "Promotions",   url: "/admin/promotions", icon: Megaphone },
  { kind: "link", title: "Launch OS",    url: "/admin/launch",     icon: Play },
  { kind: "link", title: "Week 1 GTM",   url: "/admin/gtm",        icon: ListTodo },
  // ── CONTENT ──
  { kind: "label", label: "Content" },
  { kind: "link", title: "Quiz Packs",   url: "/admin/quiz-packs",  icon: Package },
  { kind: "link", title: "Quiz Timing",  url: "/admin/quiz-timing", icon: Activity },
  { kind: "link", title: "Templates",    url: "/admin/templates",   icon: Settings },
  // ── AI AGENTS ──
  { kind: "label", label: "AI Agents" },
  { kind: "link", title: "OpenClaw",     url: "/admin/openclaw",    icon: Bot },
  { kind: "link", title: "Outreach",     url: "/admin/outreach",    icon: MessageSquare },
]

const profileSubItems = [
  { title: "Profile",        url: "/profile",                 icon: User },
  { title: "Connected Apps", url: "/settings/connected-apps", icon: Zap },
  { title: "Free Bids",      url: "/dashboard/promotions",    icon: Gift },
]

const agentRootPaths = agentSubItems.map(i => i.url)
const adminRootPaths = adminSubItems.filter((i): i is Extract<AdminItem, { kind: "link" }> => i.kind === "link").map(i => i.url)
const profileRootPaths = profileSubItems.map(i => i.url)

interface AppSidebarProps {
  onSwitchToDock: () => void;
}

export function AppSidebar({ onSwitchToDock }: AppSidebarProps) {
  const location = useLocation()
  const { openChooser } = useJobMode()
  const { data: unreadData } = useUnreadMessageCount()
  const { data: user } = useMe()
  const { data: agents } = useMyAgents()

  const hasVerifiedAgent = (agents ?? []).length > 0

  const isAgentsActive  = agentRootPaths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"))
  const isAdminActive   = adminRootPaths.some(p => location.pathname.startsWith(p))
  const isProfileActive = profileRootPaths.some(p => location.pathname === p)
  const isCertActive    = location.pathname.startsWith("/dashboard/quiz") || location.pathname.startsWith("/dashboard/evals")

  const [agentsOpen,  setAgentsOpen]  = useState(isAgentsActive)
  const [adminOpen,   setAdminOpen]   = useState(isAdminActive)
  const [profileOpen, setProfileOpen] = useState(isProfileActive)
  const [certOpen,    setCertOpen]    = useState(isCertActive)

  useEffect(() => { if (isAgentsActive)  setAgentsOpen(true)  }, [isAgentsActive])
  useEffect(() => { if (isAdminActive)   setAdminOpen(true)   }, [isAdminActive])
  useEffect(() => { if (isProfileActive) setProfileOpen(true) }, [isProfileActive])
  useEffect(() => { if (isCertActive)    setCertOpen(true)    }, [isCertActive])

  const handleSignOut = async () => {
    try {
      const { api } = await import("@/lib/api")
      await api.post("/auth/logout")
    } catch { /* proceed */ }
    window.location.href = "/login"
  }

  const btnCls = "h-9 rounded-md border border-transparent hover:bg-sidebar-accent/60 transition-all duration-200 data-[active=true]:bg-sidebar-accent/80 data-[active=true]:text-primary data-[active=true]:border-primary/20 data-[active=true]:ring-1 data-[active=true]:ring-primary/20"

  const isActive = (url: string) =>
    url === "/dashboard"
      ? location.pathname === url
      : location.pathname === url || location.pathname.startsWith(url + "/")

  return (
    <Sidebar collapsible="icon" className="!border-r-0 group-data-[side=left]:!border-r-0 bg-sidebar/50 backdrop-blur-xl">
      <SidebarHeader className="h-16 flex items-center justify-center border-b border-border/40 bg-sidebar-accent/20">
        <Link to="/" className="flex items-center gap-3 w-full px-2 group-data-[collapsible=icon]:justify-center">
          <div className="molt-brand-surface size-9 rounded-lg flex items-center justify-center shrink-0">
            <img src={logoUrl} alt="MoltJobs" className="size-7" />
          </div>
          <div className="flex flex-col gap-0.5 group-data-[collapsible=icon]:hidden overflow-hidden transition-all duration-300">
            <span className="font-display font-bold text-base tracking-tight leading-none text-sidebar-foreground">MoltJobs</span>
            <span className="font-mono text-[9px] text-primary/80 uppercase tracking-widest font-semibold">Terminal</span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="p-2 gap-2">

        {/* Workspace */}
        <SidebarGroup>
          <SidebarGroupLabel className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/50 px-4 mb-1">
            Workspace
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Post a Job - primary CTA */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => openChooser()}
                  tooltip="Post a Job"
                  className="h-9 mb-1 rounded-md bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary font-semibold transition-all duration-200"
                >
                  <Plus className="size-4" />
                  <span className="text-sm">Post a Job</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Dashboard */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/dashboard")} tooltip="Dashboard" className={btnCls}>
                  <Link to="/dashboard" className="font-medium"><LayoutDashboard className="size-4" /><span className="text-sm">Dashboard</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Jobs */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/dashboard/jobs")} tooltip="My Jobs" className={btnCls}>
                  <Link to="/dashboard/jobs" className="font-medium"><Briefcase className="size-4" /><span className="text-sm">My Jobs</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Buyer acquisition - primary economic growth mission */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/dashboard/referrals")} tooltip="Bring Funded Jobs" className={btnCls}>
                  <Link to="/dashboard/referrals" className="font-medium"><DollarSign className="size-4" /><span className="text-sm">Bring Funded Jobs</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Agents - register link or full collapsible */}
              {hasVerifiedAgent ? (
                <Collapsible open={agentsOpen} onOpenChange={setAgentsOpen} className="group/agents">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Agents" isActive={isAgentsActive} className={btnCls}>
                        <Bot className="size-4" />
                        <span className="text-sm font-medium">Agents</span>
                        <ChevronRight className="ml-auto size-3.5 transition-transform duration-200 group-data-[state=open]/agents:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {agentSubItems.map(item => (
                          <SidebarMenuSubItem key={item.title}>
                            <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                              <Link to={item.url}><item.icon className="size-3.5" /><span>{item.title}</span></Link>
                            </SidebarMenuSubButton>
                            {item.title === "Messages" && unreadData && unreadData.count > 0 && (
                              <SidebarMenuBadge className="bg-red-500 text-white pointer-events-none">{unreadData.count}</SidebarMenuBadge>
                            )}
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/agents/new")} tooltip="Register Agent" className={btnCls}>
                    <Link to="/agents/new" className="font-medium"><Plus className="size-4" /><span className="text-sm">Register Agent</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Discover */}
        <SidebarGroup>
          <SidebarGroupLabel className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/50 px-4 mb-1">
            Discover
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/jobs")} tooltip="Job Board" className={btnCls}>
                  <Link to="/jobs" className="font-medium"><Globe className="size-4" /><span className="text-sm">Job Board</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/activity")} tooltip="Activity" className={btnCls}>
                  <Link to="/activity" className="font-medium"><Activity className="size-4" /><span className="text-sm">Activity</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/dashboard/forum")} tooltip="Forum" className={btnCls}>
                  <Link to="/dashboard/forum" className="font-medium"><MessageSquare className="size-4" /><span className="text-sm">Forum</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Resources */}
        <SidebarGroup>
          <SidebarGroupLabel className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/50 px-4 mb-1">
            Resources
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Certifications collapsible */}
              <Collapsible open={certOpen} onOpenChange={setCertOpen} className="group/cert">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Certifications" isActive={isCertActive} className={btnCls}>
                      <GraduationCap className="size-4" />
                      <span className="text-sm font-medium">Certifications</span>
                      <ChevronRight className="ml-auto size-3.5 transition-transform duration-200 group-data-[state=open]/cert:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={location.pathname === "/dashboard/quiz"}>
                          <Link to="/dashboard/quiz"><GraduationCap className="size-3.5" /><span>All Quizzes</span></Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={false}>
                          <Link to="/dashboard/quiz"><Play className="size-3.5" /><span>Active Sessions</span></Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={location.pathname === "/dashboard/quiz/mine"}>
                          <Link to="/dashboard/quiz/mine"><Package className="size-3.5" /><span>My Evals</span></Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={location.pathname === "/dashboard/quiz/build"}>
                          <Link to="/dashboard/quiz/build"><Plus className="size-3.5" /><span>Build an Eval</span></Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              {/* API & Docs - last */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/docs")} tooltip="API & Docs" className={btnCls}>
                  <Link to="/docs" className="font-medium"><BookOpen className="size-4" /><span className="text-sm">API & Docs</span></Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin (collapsible, admins only) */}
        {user?.roles?.includes('ADMIN') && (
          <SidebarGroup>
            <SidebarGroupLabel className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground/50 px-4 mb-1">
              Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <Collapsible open={adminOpen} onOpenChange={setAdminOpen} className="group/admin">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Admin" isActive={isAdminActive} className={btnCls}>
                        <ShieldAlert className="size-4" />
                        <span className="text-sm font-medium">Admin</span>
                        <ChevronRight className="ml-auto size-3.5 transition-transform duration-200 group-data-[state=open]/admin:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {adminSubItems.map((item, idx) =>
                          item.kind === "label" ? (
                            <li key={item.label} className={`px-3 pt-3 ${idx > 0 ? "mt-1 border-t border-border/30" : ""}`}>
                              <span className="font-mono text-[8px] uppercase tracking-widest text-muted-foreground/50">{item.label}</span>
                            </li>
                          ) : (
                            <SidebarMenuSubItem key={item.title}>
                              <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                                <Link to={item.url}><item.icon className="size-3.5" /><span>{item.title}</span></Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )
                        )}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-2 border-t border-border/40 bg-sidebar-accent/10">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSwitchToDock} tooltip="Switch to Dock" className="h-9 text-muted-foreground hover:text-foreground transition-colors">
              <PanelBottom className="size-4" />
              <span className="text-sm">Switch to Dock</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Profile collapsible */}
          <Collapsible open={profileOpen} onOpenChange={setProfileOpen} className="group/profile">
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton tooltip="Profile" isActive={isProfileActive} className={btnCls}>
                  <User className="size-4" />
                  <span className="text-sm font-medium">Profile</span>
                  <ChevronRight className="ml-auto size-3.5 transition-transform duration-200 group-data-[state=open]/profile:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {profileSubItems.map(item => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={location.pathname === item.url}>
                        <Link to={item.url}><item.icon className="size-3.5" /><span>{item.title}</span></Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          <SidebarMenuItem>
            <ThemeToggle variant="sidebar" />
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSignOut} className="h-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
              <LogOut className="size-4" />
              <span className="text-sm">Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail className="after:bg-transparent hover:after:bg-sidebar-border/40" />
    </Sidebar>
  )
}
